/**
 *  Place for Firestore Record CRUD functions
 */

import dayjs from "dayjs"
import { Analytics, logEvent } from "firebase/analytics"
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  setDoc,
} from "firebase/firestore"
import { AmountType, Collection } from "../Enum"
import { timber_error } from "../Timber"
import { Account, Record, RecordType } from "../Type"
import { isEmptyString } from "../utils/TextUtils"

const getRecordCollection = (firestore: Firestore, userId: string) => {
  return collection(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Records}`
  )
}

const getRecordDoc = (firestore: Firestore, userId: string, recId: string) => {
  return doc(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Records}/${recId}`
  )
}

export const createRecord = async (
  firestore: Firestore,
  userId: string,
  recDate: number,
  recAmount: string | number,
  recAmountType: AmountType,
  recType: RecordType,
  recNote: string,
  recInvoiceName: string,
  recInvoiceUrl: string,
  account: Account,
  onClickClose: () => void,
  analytics: Analytics
) => {
  const currentTime = dayjs().valueOf()
  try {
    await addDoc(getRecordCollection(firestore, userId), {
      date: recDate,
      amount: isEmptyString(recAmount) ? 0 : (recAmount as number) * 100,
      credit: recAmountType === AmountType.Credit,
      recordType: recType.name,
      recordTypeId: recType.id,
      note: recNote,
      account: account.name,
      accountId: account?.id,
      invoiceName: isEmptyString(recInvoiceName) ? "" : recInvoiceName,
      invoiceUri: isEmptyString(recInvoiceUrl) ? "" : recInvoiceUrl,
      insertDate: currentTime,
      lastEditDate: currentTime,
    } as Record)
    onClickClose()
    logEvent(analytics, "web_rec_created")
  } catch (error) {
    timber_error(error)
    logEvent(analytics, "web_rec_create_error")
    onClickClose()
  }
}

export const editRecord = async (
  firestore: Firestore,
  userId: string,
  recId: string | undefined,
  recDate: number,
  recAmount: string | number,
  recAmountType: AmountType,
  recType: RecordType,
  recNote: string,
  recInvoiceName: string,
  recInvoiceUrl: string,
  account: Account,
  insertDate: number,
  onClickClose: () => void,
  analytics: Analytics
) => {
  if (isEmptyString(userId)) {
    logEvent(analytics, "web_user_id_empty")
    onClickClose()
  } else if (isEmptyString(recId)) {
    logEvent(analytics, "web_rec_id_empty")
    onClickClose()
  } else {
    const currentTime = dayjs().valueOf()
    try {
      await setDoc(
        getRecordDoc(firestore, userId, recId!!),
        {
          date: recDate,
          amount: isEmptyString(recAmount) ? 0 : (recAmount as number) * 100,
          credit: recAmountType === AmountType.Credit,
          recordType: recType.name,
          recordTypeId: recType.id,
          note: recNote,
          account: account.name,
          accountId: account?.id,
          invoiceName: isEmptyString(recInvoiceName) ? "" : recInvoiceName,
          invoiceUri: isEmptyString(recInvoiceUrl) ? "" : recInvoiceUrl,
          insertDate: insertDate,
          lastEditDate: currentTime,
        } as Record,
        { merge: true }
      )
      onClickClose()
      logEvent(analytics, "web_rec_edited")
    } catch (error) {
      timber_error(error)
      logEvent(analytics, "web_rec_edit_error")
      onClickClose()
    }
  }
}

export const deleteRecord = async (
  firestore: Firestore,
  userId: string,
  recId: string | undefined,
  onClickClose: () => void,
  analytics: Analytics
) => {
  if (isEmptyString(userId)) {
    logEvent(analytics, "web_user_id_empty")
    onClickClose()
  } else if (isEmptyString(recId)) {
    logEvent(analytics, "web_rec_id_empty")
    onClickClose()
  } else {
    try {
      await deleteDoc(
        doc(firestore, getRecordCollection(firestore, userId).path, recId!!)
      )
      logEvent(analytics, "web_rec_deleted")
      onClickClose()
    } catch (error) {
      timber_error(error)
      logEvent(analytics, "web_rec_delete_error")
      onClickClose()
    }
  }
}
